import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Menu, MenuItem } from "semantic-ui-react";

import { DBBTyreSearch, DBB_TYRE_STATES } from "components/DBB/components/DBBCreateContractModal/components/DBBTyreSearch";
import "components/DBB/components/DBBCreateContractModal/components/DBBTyreSetsSection/DBBTyreSetsSection.scss";
import { useDBBContractOptions } from "components/DBB/components/DBBCreateContractModal/hooks";
import { ReactTable } from "components/ReactTable";
import { createFormHelper } from "lib/Form";
import { getTyreDescription, getTyreSize } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { getTyreSeasonIcon } from "utils/common";

import type { DBBContractFormPayload } from "components/DBB/components/DBBCreateContractModal/components/DBBCreateContractForm";
import type { TFunction } from "i18next";
import type { Car, CarTyres, Tyre } from "models";

export enum DBB_TYRE_SET_TYPES {
  OnCar = "ON_CAR",
  Storage = "STORAGE"
}

export enum DBB_WHEEL_TYPES {
  None = "N",
  Steel = "S",
  Alloy = "A"
}

export enum DBB_TYRE_POSITIONS {
  FrontLeft = "FL",
  FrontRight = "FR",
  RearLeft = "RL",
  RearRight = "RR"
}

export type DBBContractSetTyre = {
  tyre: Tyre;
  position: DBB_TYRE_POSITIONS;
  profile: number | null;
};

export type DBBContractTyreSet = {
  set_type: DBB_TYRE_SET_TYPES;
  wheel_type: DBB_WHEEL_TYPES | null;
  tyres: DBBContractSetTyre[];
};

type DBBTyreSetsSectionProps = {
  car: Car;
};

type AddTyreTarget = {
  setIndex: number;
  position: DBB_TYRE_POSITIONS;
};

type TyreSetRow = {
  position: DBB_TYRE_POSITIONS;
  positionName: string;
  tyreIndex: number;
  setTyre?: DBBContractSetTyre;
};

const DEFAULT_PROFILE = 10;
const FormHelper = createFormHelper<DBBContractFormPayload>();
const columnHelper = createColumnHelper<TyreSetRow>();

const SET_LABELS: Record<DBB_TYRE_SET_TYPES, { label: string; defaultText: string }> = {
  [DBB_TYRE_SET_TYPES.OnCar]: { label: "v8_on_car", defaultText: "On Car" },
  [DBB_TYRE_SET_TYPES.Storage]: { label: "v8_storage", defaultText: "Storage" }
};

export const SET_STATES: Record<DBB_TYRE_SET_TYPES, DBB_TYRE_STATES> = {
  [DBB_TYRE_SET_TYPES.OnCar]: DBB_TYRE_STATES.OnCar,
  [DBB_TYRE_SET_TYPES.Storage]: DBB_TYRE_STATES.OnStorage
};

const POSITIONS: { position: DBB_TYRE_POSITIONS; label: string; defaultText: string }[] = [
  { position: DBB_TYRE_POSITIONS.FrontLeft, label: "v8_front_left", defaultText: "Front Left" },
  { position: DBB_TYRE_POSITIONS.FrontRight, label: "v8_front_right", defaultText: "Front Right" },
  { position: DBB_TYRE_POSITIONS.RearLeft, label: "v8_rear_left", defaultText: "Rear Left" },
  { position: DBB_TYRE_POSITIONS.RearRight, label: "v8_rear_right", defaultText: "Rear Right" }
];

const CAR_TYRE_FIELDS = [
  { position: DBB_TYRE_POSITIONS.FrontLeft, tyreField: "front_left_tyre", profileField: "fl_profile" },
  { position: DBB_TYRE_POSITIONS.FrontRight, tyreField: "front_right_tyre", profileField: "fr_profile" },
  { position: DBB_TYRE_POSITIONS.RearLeft, tyreField: "rear_left_tyre", profileField: "rl_profile" },
  { position: DBB_TYRE_POSITIONS.RearRight, tyreField: "rear_right_tyre", profileField: "rr_profile" }
] as const;

const getCarTyres = (car: Car, setType: DBB_TYRE_SET_TYPES) => (setType === DBB_TYRE_SET_TYPES.OnCar ? car.on_car_tyres : car.in_storage_tyres);

const toSetTyres = (carTyres?: CarTyres): DBBContractSetTyre[] => {
  if (!carTyres) return [];

  return CAR_TYRE_FIELDS.reduce<DBBContractSetTyre[]>((setTyres, { position, tyreField, profileField }) => {
    const tyre = carTyres[tyreField];
    if (tyre) setTyres.push({ tyre, position, profile: carTyres[profileField] });

    return setTyres;
  }, []);
};

export const getDefaultTyreSets = (car: Car): DBBContractTyreSet[] =>
  [DBB_TYRE_SET_TYPES.OnCar, DBB_TYRE_SET_TYPES.Storage].map(set_type => ({
    set_type,
    wheel_type: null,
    tyres: toSetTyres(getCarTyres(car, set_type))
  }));

export const MINIMUM_COMPLETE_TYRE_SETS = 2;

export const countCompleteTyreSets = (tyreSets: DBBContractTyreSet[] = []) =>
  tyreSets.filter(({ wheel_type, tyres }) => wheel_type && POSITIONS.every(({ position }) => tyres.some(tyre => tyre.position === position))).length;

const getWheelTypeOptions = (t: TFunction) => [
  { key: DBB_WHEEL_TYPES.None, value: DBB_WHEEL_TYPES.None, text: t("v8_none", "None") },
  { key: DBB_WHEEL_TYPES.Steel, value: DBB_WHEEL_TYPES.Steel, text: t("v8_steel", "Steel") },
  { key: DBB_WHEEL_TYPES.Alloy, value: DBB_WHEEL_TYPES.Alloy, text: t("v8_alloy", "Alloy") }
];

export const DBBTyreSetsSection = ({ car }: DBBTyreSetsSectionProps) => {
  const { t } = useTranslation();

  const [activeSetType, setActiveSetType] = useState(DBB_TYRE_SET_TYPES.OnCar);
  const [addTyreTarget, setAddTyreTarget] = useState<AddTyreTarget | null>(null);

  const { storageLocationOptions, isFetchingStorageLocations } = useDBBContractOptions(car);

  const { watch, setValue, getValues, formState } = FormHelper.useFormContext();
  const tyreSets = watch("tyre_sets");
  const storageLocationId = watch("storage_location_id");

  const activeSetIndex = tyreSets.findIndex(set => set.set_type === activeSetType);
  const activeSet = activeSetIndex < 0 ? undefined : tyreSets[activeSetIndex];

  const updateSet = useCallback((setIndex: number, tyres: DBBContractSetTyre[]) => setValue(`tyre_sets.${setIndex}.tyres`, tyres), [setValue]);

  const handleConfirmTyre = (pickedTyre: Tyre) => {
    if (!addTyreTarget) return;

    const { setIndex, position } = addTyreTarget;
    updateSet(setIndex, [...tyreSets[setIndex].tyres.filter(tyre => tyre.position !== position), { tyre: pickedTyre, position, profile: DEFAULT_PROFILE }]);
  };

  const handleRemoveTyre = useCallback(
    (setIndex: number, position: DBB_TYRE_POSITIONS) =>
      updateSet(
        setIndex,
        getValues(`tyre_sets.${setIndex}.tyres`).filter(tyre => tyre.position !== position)
      ),
    [getValues, updateSet]
  );

  const handleCopyOverSet = useCallback(
    (setIndex: number, tyreToCopy: DBBContractSetTyre) =>
      updateSet(
        setIndex,
        POSITIONS.map(({ position }) => ({ ...tyreToCopy, position }))
      ),
    [updateSet]
  );

  const tyreSetRows: TyreSetRow[] = activeSet
    ? POSITIONS.map(({ position, label, defaultText }) => {
        const tyreIndex = activeSet.tyres.findIndex(tyre => tyre.position === position);

        return { position, positionName: t(label, defaultText), tyreIndex, setTyre: tyreIndex < 0 ? undefined : activeSet.tyres[tyreIndex] };
      })
    : [];

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "position",
        header: () => t("v8_position", "Position"),
        size: 120,
        cell: ({
          row: {
            original: { position, positionName }
          }
        }) => (
          <div className="tyre-position">
            <div>
              <Icon className="plus circle" color="blue" onClick={() => setAddTyreTarget({ setIndex: activeSetIndex, position })} />
            </div>
            <span>{positionName}</span>
          </div>
        )
      }),
      columnHelper.accessor(row => row, {
        id: "size",
        header: () => t("v8_size", "Size"),
        size: 110,
        cell: ({
          row: {
            original: { setTyre }
          }
        }) =>
          setTyre && (
            <div className="tyre-size">
              <div>
                <Icon className={getTyreSeasonIcon(setTyre.tyre.season)} />
              </div>
              {getTyreSize(setTyre.tyre)}
            </div>
          )
      }),
      columnHelper.accessor(row => row.setTyre?.tyre, {
        id: "description",
        header: () => t("v8_description", "Description"),
        size: 350,
        cell: cell => getTyreDescription(cell.getValue())
      }),
      columnHelper.accessor(row => row.setTyre?.tyre.ean, {
        id: "ean",
        header: () => "EAN",
        size: 140,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row.setTyre?.tyre.manufacturer, {
        id: "brand",
        header: () => t("v8_brand", "Brand"),
        size: 110,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor(row => row, {
        id: "profile",
        header: () => t("v8_profile", "Profile"),
        size: 80,
        cell: ({
          row: {
            original: { setTyre, tyreIndex }
          }
        }) =>
          setTyre && (
            <div className="tyre-profile">
              <FormHelper.Field.Input
                name={`tyre_sets.${activeSetIndex}.tyres.${tyreIndex}.profile`}
                nullable
                inputProps={{ placeholder: t("v8_profile", "Profile"), type: "number" }}
              />
              <span>mm</span>
            </div>
          )
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        header: () => null,
        size: 120,
        cell: ({
          row: {
            original: { setTyre, position }
          }
        }) =>
          setTyre && (
            <div className="tyre-actions">
              <Button className="green" icon type="button" onClick={() => handleCopyOverSet(activeSetIndex, setTyre)}>
                <Icon className="copy" />
              </Button>
              <Button className="light" icon type="button" onClick={() => handleRemoveTyre(activeSetIndex, position)}>
                <Icon className="trash red" />
              </Button>
            </div>
          )
      })
    ],
    [t, activeSetIndex, handleCopyOverSet, handleRemoveTyre]
  );

  return (
    <div className="contract-form-tyre-sets-section">
      <label className="big tyre-sets-section-label">
        <Icon className="tire" />
        {t("v8_tyre_sets", "Tyre sets")}
      </label>

      <Menu tabular className="tyre-set-tabs">
        {tyreSets.map(set => {
          const hasError = formState.isSubmitted && (!set.wheel_type || (set.set_type === DBB_TYRE_SET_TYPES.Storage && !storageLocationId));

          return (
            <MenuItem
              key={set.set_type}
              active={set.set_type === activeSetType}
              className={hasError ? "has-error" : undefined}
              onClick={() => setActiveSetType(set.set_type)}
            >
              {t(SET_LABELS[set.set_type].label, SET_LABELS[set.set_type].defaultText)}
              {hasError && <Icon className="exclamation circle" color="red" />}
            </MenuItem>
          );
        })}
      </Menu>

      {activeSet && (
        <div className="tyre-set" key={activeSet.set_type}>
          <Form.Group>
            <Form.Field width={3} className="wheel-type-field" required error={formState.isSubmitted && !activeSet.wheel_type}>
              <label>{t("v8_wheel_type", "Wheel type")}</label>
              <FormHelper.Field.Dropdown
                name={`tyre_sets.${activeSetIndex}.wheel_type`}
                options={getWheelTypeOptions(t)}
                dropdownProps={{ selectOnBlur: false, placeholder: t("v8_select_wheel_type", "Select wheel type") }}
              />
            </Form.Field>
            {activeSet.set_type === DBB_TYRE_SET_TYPES.Storage && (
              <Form.Field width={4} required error={formState.isSubmitted && !storageLocationId}>
                <label>{t("v8_storage_location", "Storage location")}</label>
                <FormHelper.Field.Dropdown
                  name="storage_location_id"
                  options={storageLocationOptions}
                  dropdownProps={{
                    selection: true,
                    search: true,
                    selectOnBlur: false,
                    loading: isFetchingStorageLocations,
                    placeholder: t("v8_storage_location", "Storage location")
                  }}
                />
              </Form.Field>
            )}
          </Form.Group>

          <ReactTable data={tyreSetRows} columns={columns} />
        </div>
      )}

      <DBBTyreSearch car={car} open={!!addTyreTarget} onClose={() => setAddTyreTarget(null)} onConfirm={handleConfirmTyre} />
    </div>
  );
};
